import { Box, Typography, IconButton, Button } from '@mui/material'
import { FC, useRef, useState } from 'react'
import symptom_icon from '~/assets/images/symptom_icon.svg'
import { components } from 'react-select'
import debounce from 'debounce-promise'
import AsyncSelect from 'react-select/async'
import type { ControlProps, SelectInstance } from 'react-select'
import { VisitReason } from '../AppointmentService'
import type { AppointmentSymptomsRetrieveResponse } from '@fireflyhealth/api'
import Search from '@mui/icons-material/Search'
import Close from '@mui/icons-material/Close'
import Fuse from 'fuse.js'
import { useFocusSearchHotkey } from '~/shared/hooks/useFocusSearchHotkey'

/**
 * A concern the picker can offer or hold as selected. Looser than the wire's
 * `Symptom` because a free-text concern the member typed has no id.
 */
export type SymptomItem = {
  id?: number
  label: string
}

/** One catalogued symptom, exactly as `/appointment/symptoms/` returns it. */
export type Symptom = AppointmentSymptomsRetrieveResponse['all_symptoms'][number]

/** A browse-by-category grouping of catalogued symptoms, as the same endpoint returns it. */
export type SymptomCategory = AppointmentSymptomsRetrieveResponse['home_page_categories'][number]

type SelectOption = {
  label: string
  id?: number | null
  value?: number | null
  category_uid?: string | null
}

type PrimaryConcernProps = {
  setIsLoading: (e: boolean) => void
  isLoading: boolean
  setSymptomsSelected: (e: SymptomItem[]) => void
  symptomsSelected: SymptomItem[]
  setOtherSymptomsSelected: (e: string[]) => void
  otherSymptomsSelected: string[]
  setVisitReason: (value: VisitReason | null) => void
  patientId: number
  allSymptoms: Symptom[]
  setAllSymptoms: (e: Symptom[]) => void
  categories: SymptomCategory[]
}
const Control = (props: ControlProps<SelectOption, false>) => {
  return (
    <components.Control {...props}>
      <Search sx={{ ml: 1, color: 'blueGrey.main' }} />
      {props.children}
    </components.Control>
  )
}

const fuseOptions = {
  isCaseSensitive: false,
  distance: 100,
  findAllMatches: false,
  includeMatches: false,
  includeScore: false,
  location: 0,
  minMatchCharLength: 0,
  shouldSort: true,
  threshold: 0.6,
  useExtendedSearch: false,
  keys: ['label'],
}

const makeSelectOptions = (symptoms: SymptomItem[]) =>
  symptoms.map(symptom => ({
    ...symptom,
    value: symptom.id,
    label: `${symptom.label}`,
  }))
const SEARCH_DEBOUNCE_TIME = 300

const PrimaryConcern: FC<PrimaryConcernProps> = ({
  isLoading,
  otherSymptomsSelected,
  setOtherSymptomsSelected,
  symptomsSelected,
  setSymptomsSelected,
  allSymptoms,
  categories,
}) => {
  const selectRef = useRef<SelectInstance<SelectOption, false> | null>(null)
  const [selectableSymptoms, setSelectableSymptoms] = useState<SymptomItem[]>(allSymptoms)

  // A new catalog from the parent resets which symptoms are still selectable.
  const [catalogSymptoms, setCatalogSymptoms] = useState(allSymptoms)
  if (allSymptoms !== catalogSymptoms) {
    setCatalogSymptoms(allSymptoms)
    setSelectableSymptoms(allSymptoms)
  }

  useFocusSearchHotkey(event => {
    event.preventDefault()
    selectRef.current?.focus()
  })

  type FuseSearchResult = SelectOption
  const debouncedSearch = debounce(
    (searchTermInput: string) => {
      const options = makeSelectOptions(selectableSymptoms) as SelectOption[]
      const fuse = new Fuse(options, fuseOptions)
      const results: FuseSearchResult[] = fuse.search(searchTermInput).map(r => r.item)
      const resultMatchingLabel = results.find(result => result.label === searchTermInput)
      if (!resultMatchingLabel) {
        results.push({
          label: searchTermInput,
          id: null,
          category_uid: null,
          value: null,
        })
      }
      return results
    },
    SEARCH_DEBOUNCE_TIME,
    { leading: true },
  )

  const removeSymptom = (label: string) => {
    const selectedSymptoms = symptomsSelected.filter(item => {
      return item.label !== label
    })
    // Make sure the removed symptom is selectable again,
    // but a free text symptom removed need not be shown in the drop down of selectable symptoms
    if (symptomsSelected.length != selectedSymptoms.length) {
      setSelectableSymptoms([...selectableSymptoms, { label }])
    }
    setSymptomsSelected(selectedSymptoms)
    const otherSymptoms = otherSymptomsSelected.filter(item => {
      return item !== label
    })
    setOtherSymptomsSelected(otherSymptoms)
  }

  const handleSymptomSelection = (e: SelectOption) => {
    // Show only remaining symptoms, to avoid selection of same symptom again
    const filtered = selectableSymptoms.filter(obj => {
      if (obj.label !== e.label) {
        return obj
      }
    })
    setSelectableSymptoms(filtered)
    if (e.id) {
      setSymptomsSelected([{ id: e.id, label: e.label }, ...symptomsSelected])
    } else {
      setOtherSymptomsSelected([e.label, ...otherSymptomsSelected])
    }
  }

  const loadOptions = (input: string) => {
    return debouncedSearch(input)
  }

  let symptomsList: string[] = symptomsSelected.map(({ label }) => label)
  if (otherSymptomsSelected.length) {
    symptomsList = symptomsList.concat(otherSymptomsSelected)
  }

  return (
    <>
      {!isLoading ? (
        <>
          <Box sx={{ mx: 2, my: 2, display: 'flex' }}>
            <Box sx={{ display: 'inline-flex' }}>
              <AsyncSelect
                value={null}
                onChange={e => {
                  if (e) handleSymptomSelection(e)
                }}
                placeholder="Add symptom or condition"
                components={{ Control }}
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    display: 'inline-flex',
                    width: 500,
                    height: 40,
                    borderRadius: 5,
                  }),
                  indicatorSeparator: () => ({ display: 'none' }),
                  dropdownIndicator: () => ({ display: 'none' }),
                }}
                defaultOptions={[
                  {
                    options: makeSelectOptions(selectableSymptoms),
                  },
                ]}
                loadOptions={loadOptions}
                openMenuOnClick
                openMenuOnFocus
                tabSelectsValue={false}
                ref={selectRef}
              />
            </Box>
          </Box>
          {symptomsList.length ? (
            <Box sx={{ height: 380 }}>
              {symptomsList.map(symptom => {
                return (
                  <Box
                    key={symptom}
                    sx={{
                      mx: 2,
                      my: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ justifyContent: 'left', display: 'flex' }}>
                      <img
                        src={symptom_icon}
                        alt="Symptom"
                        style={{ width: 24, height: 24, marginRight: 8 }}
                      />
                      <Typography
                        sx={{
                          fontWeight: '400',
                          fontSize: 16,
                        }}
                      >
                        {symptom}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <IconButton
                        aria-label={`Remove ${symptom}`}
                        onClick={() => {
                          removeSymptom(symptom)
                        }}
                        size="small"
                      >
                        <Close />
                      </IconButton>
                    </Box>
                  </Box>
                )
              })}
              <Box sx={{ marginLeft: 2, marginBottom: 3, alignContent: 'top' }}>
                <Typography
                  sx={{
                    fontWeight: '400',
                    fontSize: 14,
                    color: 'text.secondary',
                  }}
                >
                  Search again to add multiple concerns
                </Typography>
              </Box>
            </Box>
          ) : (
            categories.map(category => (
              <Box key={category.id} sx={{ my: 2, mx: 2 }}>
                <Typography sx={{ fontWeight: '600', color: 'text.secondary', fontSize: 14 }}>
                  {category.label}
                </Typography>
                {category.symptoms.map(symptom => {
                  return (
                    <Box
                      key={symptom.id}
                      sx={{ my: 1, display: 'flex', alignItems: 'center', justifyContent: 'left' }}
                    >
                      <img
                        src={symptom_icon}
                        alt="Symptom"
                        style={{ width: 24, height: 24, marginRight: 6 }}
                      />
                      <Button
                        color="inherit"
                        onClick={() => {
                          handleSymptomSelection(symptom)
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: '400',
                            fontSize: 16,
                          }}
                        >
                          {symptom.label}
                        </Typography>
                      </Button>
                    </Box>
                  )
                })}
              </Box>
            ))
          )}
        </>
      ) : null}
    </>
  )
}

export default PrimaryConcern
